/**
 * A CHANGE OF AGED CARE HOME, in the client's words (owner, 7 Oct 2026: common; "consider how we
 * explain things"). The person is in care now; every plan on the page is measured against STAYING
 * PUT, and the move is what the plans do. One module for the words, so step 1, step 2, the plan
 * summary, the cards, the recap and the advice document all say the same thing.
 */
import { formatCurrency } from '../../utils/index.ts';

/** A daily figure keeps its cents: $23.32 a day is a real quote, and "$23" would understate the year. */
const perDay = (n: number): string => `$${(Math.round(n * 100) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface CurrentHomeFacts {
    name: string;
    facilityName: string;
    roomPrice: number;
    /** The deposit as paid, and what it would be refunded at today. */
    paid: number;
    refundToday: number;
    retained: number;
    /** The daily payment on the unpaid part of the room, a day. */
    dailyPayment: number;
    /** "January 2024" */
    entryLabel: string;
    grandfathered: boolean;
}

export function stayPutHeading(facts: Pick<CurrentHomeFacts, 'facilityName'>): string {
    return `Staying at ${facts.facilityName || 'the current home'}. Four numbers tell the story.`;
}

export function stayPutIntro(facts: Pick<CurrentHomeFacts, 'name' | 'facilityName'>): string {
    return `This is the stay-put position: what the next year looks like if ${facts.name} stays at ${facts.facilityName || 'the current home'} and nothing changes. Every plan in step 3 — including a move — is measured against it.`;
}

/** Step 1's summary of the home the person is in now. */
export function currentHomeSummary(f: CurrentHomeFacts): string {
    const deposit = f.paid > 0
        ? f.retained > 0.5
            ? `a deposit of ${formatCurrency(f.paid)} was paid, which would be refunded at about ${formatCurrency(Math.round(f.refundToday))} today (${formatCurrency(Math.round(f.retained))} has been retained so far)`
            : `a deposit of ${formatCurrency(f.paid)} was paid, refundable in full${f.grandfathered ? ' — no retention applies, as care began before 1 November 2025' : ''}`
        : 'no deposit was paid';
    const dap = f.dailyPayment > 0 ? `, and ${perDay(f.dailyPayment)} a day is paid on the rest of the room` : '';
    return `${f.name} has been at ${f.facilityName || 'the current home'} since ${f.entryLabel || 'entering care'}: the room was priced at ${formatCurrency(f.roomPrice)}, ${deposit}${dap}. Staying there is what every plan below is compared against.`;
}

/** The funding card's first line: the refund goes to the new room before anything is sold. */
export function refundFirstLine(f: Pick<CurrentHomeFacts, 'name' | 'paid' | 'refundToday' | 'retained'>): string {
    if (f.paid <= 0) return `${f.name} paid no deposit at the current home, so the whole of the new room's deposit comes from savings.`;
    const less = f.retained > 0.5 ? ` (${formatCurrency(f.paid)} paid, less ${formatCurrency(Math.round(f.retained))} retained so far)` : '';
    return `${formatCurrency(Math.round(f.refundToday))} comes back from the current deposit on the move${less} and goes to the new room first. Only what the new room costs beyond that comes from savings.`;
}

export interface MovePieceFacts {
    name: string;
    facilityName: string;
    moveLabel: string;
    newRoomPrice: number;
    deposit: number;
    fromRefund: number;
    fromSale: number;
    /** "$50,000 from anz and $44,000 from shares", or '' */
    soldPhrase: string;
    dapPerDay: number;
    grandfathered: boolean;
    retentionDaysTaken: number;
}

/** The recap's and the advice document's paragraph on the move. */
export function movePiece(f: MovePieceFacts): string {
    const unpaid = Math.max(0, f.newRoomPrice - f.deposit);
    const paidWith = f.deposit <= 0
        ? `nothing is paid up front for the new ${formatCurrency(f.newRoomPrice)} room, so the whole of it is charged as a daily payment`
        : `${formatCurrency(Math.round(f.deposit))} of the new ${formatCurrency(f.newRoomPrice)} room is paid up front — ${formatCurrency(Math.round(f.fromRefund))} from the refund of the current deposit${f.fromSale > 0.5 ? `, and ${formatCurrency(Math.round(f.fromSale))} ${f.soldPhrase ? `from savings (${f.soldPhrase})` : 'from savings'}` : ''}${unpaid > 0.5 ? `; the unpaid ${formatCurrency(Math.round(unpaid))} is charged as a daily payment of ${perDay(f.dapPerDay)} a day` : ''}`;
    const retention = f.grandfathered
        ? 'No retention is taken from the deposit, at either home: care began before 1 November 2025.'
        : f.retentionDaysTaken > 0
            ? `The 2% a year retention carries on from where it is (${describeYears(f.retentionDaysTaken)} of the five already counted); it does not start again at the new home.`
            : 'The 2% a year retention runs for the first five years of the deposit, at whichever home holds it.';
    return `${f.name} moves from ${f.facilityName || 'the current home'} in ${f.moveLabel}: ${paidWith}. The fees switch to the new home's on the day of the move, and the yearly and lifetime caps carry on from where they are. ${retention}`;
}

export const STAY_PUT_NAME = 'Stay where you are';

function describeYears(days: number): string {
    const years = Math.round((days / 365) * 10) / 10;
    if (years < 1) return `${Math.round(days / 30)} months`;
    return `${years} ${years === 1 ? 'year' : 'years'}`;
}
